/**
 * The dark plan card's price, split into the two runs the renderer draws (WAT-11988 AC8).
 *
 * The blok authors ONE string — `[$<INTERNET_CHOICE_OFFERPRICE>/mo.* || see details]`,
 * painted to `$64.99/mo.*` by `injectDynamicValues` — because a token resolves to one
 * value and there is no dollars-only token. `PlanCard` needs TWO props, which it
 * forwards to the shared `PriceBlock` molecule (`dollars` required, `cents` optional).
 *
 * ⚠ NOT the light card's rule, and it cannot be. The light card splits on the first `.`
 * (`dtv-plan-card-v2.js:321-322`), but its `modifiedProductPrice` is a BARE price — the
 * `/mo.` is separate `priceCopy`. Ours carries the frequency in the same string, so
 * splitting on the first `.` turns the whole-dollar "$64/mo.*" into dollars "$64/mo".
 * We cut at the end of the leading DIGIT RUN instead. A whole-dollar price is real
 * here: `formatPrice` is deliberately not applied to OFFERPRICE
 * (`dynamic-injection/price.ts`), so a feed value of 64 reaches the blok as "64".
 *
 * Its OWN module rather than the adapter's, because both halves of the v3 plan path
 * need it and only one can import the adapter — the Visual Editor skips the
 * request-time adapter and re-adapts in the browser. Importing the adapter here would
 * pull `next/server` in through the personalization graph.
 *
 * ⚠ NEVER returns null for a non-empty string, and that is load-bearing.
 * `price_dollars` is REQUIRED by `PlanCard`, which returns null without one
 * (`plan-card.tsx:306`) — so a null here does not leave the card alone, it DELETES the
 * card, server-side and in the Visual Editor, with nothing logged. Copy that does not
 * match the money shape is common and legitimate: "Free", "$64.99/mo. for 12 mos.",
 * "$64.99/mo. + $13.99 RSN" (a second number in the suffix), an author's
 * `|| see details` fallback. All of those fall back to the WHOLE string in the dollars
 * run — the same thing an author gets by typing it into `price_dollars` directly.
 *
 * So the only question this answers is WHERE TO CUT, not whether to render. Prose with
 * a number in it ("2 yrs $64.99") is still not cut at its first digit — it falls back
 * whole rather than drawing a lone "2" at the price size.
 *
 * Null is reserved for "there is no authored price at all": empty, whitespace, or not
 * a string. Only then does the caller leave the card's own props alone.
 */
export function splitAuthoredPrice(price: unknown): { price_dollars: string; price_cents?: string } | null {
  if (typeof price !== "string") {
    return null;
  }
  const trimmed = price.trim();
  if (trimmed === "") {
    return null;
  }
  /* ONE number, then a suffix carrying no further digits. Anything else is drawn
     whole rather than cut in the wrong place — see the warning above: a null here
     would delete the card, not decline to split it. */
  const split = /^([^0-9A-Za-z]*[\d,]*\d)(\.\d+)?([^0-9]*)$/.exec(trimmed);
  if (!split) {
    return { price_dollars: trimmed };
  }
  const cents = `${split[2] ?? ""}${split[3] ?? ""}`;
  return {
    price_dollars: split[1],
    ...(cents === "" ? {} : { price_cents: cents })
  };
}
